import { useEffect, useMemo, useState } from 'react';
import { getListaServicosPrestados } from 'api/forms/pacote_formularios.api';
import { useFormularioSecretariaMunicipalAgriculturaStore } from '../../../../../../stores/forms/formularioAgricultura.store';

/**
 * Ordem canônica dos serviços prestados conforme regras de negócio.
 * "Nenhum" e "Outros" são tratados separadamente no componente.
 * Os rótulos devem seguir a ordem abaixo (entre "Nenhum" e "Outros").
 */
const ORDEM_SERVICOS_PRESTADOS = [
  'Assistência / Orientação Técnica',
  'Assistência Técnica e Extensão Rural - ATER',
  'Horas-trator / Máquina (Com custo)',
  'Horas-trator / Máquina (Gratuitamente)',
  'Fornecimento de Insumos (Calcário, fertilizantes, sementes, mudas)',
  'Apoio à elaboração de projetos de crédito rural',
  'Realização de eventos (Dias de campo, cursos, seminários)',
  'Associativismo e Cooperativismo',
  'Acesso à água para consumo',
  'Irrigação',
  'Avicultura',
  'Apicultura',
  'Orizicultura (Arroz na agricultura familiar)',
  'Cacauicultura',
  'Cafeicultura',
  'Floricultura',
  'Fruticultura',
  'Mandiocultura',
  'Olericultura',
  'Pecuária de Leite',
  'Pecuária de Corte',
  'Piscicultura',
  'Sistemas Agroflorestais (SAFs)',
  'Plantas Medicinais, Aromáticas e Condimentares',
  'Produção Agroecológica',
  'Produção Orgânica',
  'Serviço de Inspeção Municipal - SIM',
  'Apoio à realização de feiras (Livres e/ou Permanentes)',
  'Apoio ao PNAE (Merenda Escolar)',
  'Apoio ao PAA (Aquisição de Alimentos Federal)',
  'Programa Municipal de Aquisição de Alimentos (Recursos Próprios)',
  'Compras Governamentais Municipais',
  'Comercialização em Canais Convencionais (Mercados, Hotéis)',
  'Comercialização Eletrônica (Sites, Apps)',
  'Apoio na emissão de Nota Fiscal',
  'Declaração de Produção para Inscrição Estadual',
  'Ações para Juventude Rural',
  'Ações para Mulheres Rurais',
  'Ações para Povos Indígenas',
  'Ações para Quilombolas',
  'Ações para Outros Povos e Comunidades',
  'Artesanato Rural',
  'Geoprocessamento',
  'Licenciamento Ambiental',
  'Cadastro Ambiental Rural - CAR',
  'Preservação Ambiental',
  'Extrativismo e Sociobiodiversidade',
  'Manejo Ambiental',
  'Recuperação de Áreas Degradadas',
  'Economia Solidária',
  'Segurança Alimentar e Nutricional',
];

/**
 * Normaliza texto para comparação: lowercase, remove acentos,
 * colapsa espaços e remove caracteres especiais (exceto letras/números/espaços).
 */
const normalizar = (texto: string): string =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .replace(/[^a-z0-9\s]/g, ' ')   // substitui pontuação/símbolos por espaço
    .replace(/\s+/g, ' ')           // colapsa espaços
    .trim();

const ORDEM_NORMALIZADA = ORDEM_SERVICOS_PRESTADOS.map(normalizar);

/**
 * Mapeamento explícito de nomes verbosos da API para rótulos canônicos.
 * As chaves são os nomes NORMALIZADOS (sem acentos, lowercase, sem pontuação)
 * dos nomes retornados pela API. Os valores são os rótulos que devem aparecer na UI.
 *
 * Nomes da API que já coincidem com os rótulos canônicos são tratados automaticamente
 * pela função ORDEM_SERVICOS_PRESTADOS (match direto). Aqui ficam SOMENTE os que diferem.
 */
const MAPEAMENTO_API: Record<string, string> = {
  // Horas-trator (API usa "Disponibilização de horas-trator/máquina")
  'disponibilizacao de horas trator maquina com custo':
    'Horas-trator / Máquina (Com custo)',
  'disponibilizacao de horas trator maquina gratuitamente':
    'Horas-trator / Máquina (Gratuitamente)',

  // Insumos (API usa "Disponibilização de insumos")
  'disponibilizacao de insumos calcario fertilizantes sementes mudas etc':
    'Fornecimento de Insumos (Calcário, fertilizantes, sementes, mudas)',

  // Cadeias produtivas (API usa "Programa/projeto/ação voltada à cadeia produtiva da/do ...")
  'programa projeto acao voltada a cadeia produtiva do arroz na agricultura familiar':
    'Orizicultura (Arroz na agricultura familiar)',
  'programa projeto acao voltada a cadeia produtiva da mandioca':
    'Mandiocultura',

  // PNAE / PAA (API usa "Apoio à elaboração de projetos para o ...")
  'apoio a elaboracao de projetos para o programa nacional de alimentacao escolar pnae':
    'Apoio ao PNAE (Merenda Escolar)',
  'apoio a elaboracao de propostas para o programa de aquisicao de alimentos paa':
    'Apoio ao PAA (Aquisição de Alimentos Federal)',

  // Programa Municipal (API usa texto longo com "com recursos próprios")
  'programa municipal de aquisicao de alimentos com recursos proprios':
    'Programa Municipal de Aquisição de Alimentos (Recursos Próprios)',

  // Compras governamentais (API usa texto longo completo)
  'programa municipal de compras governamentais aquisicao de produtos da agricultura familiar para atender os orgaos da administracao publica municipal':
    'Compras Governamentais Municipais',

  // Comercialização convencional (API usa "Programa/projeto/ação voltada à comercialização ... convencionais")
  'programa projeto acao voltada a comercializacao dos produtos da agricultura familiar nos canais de comercializacao convencionais supermercados quitandas restaurantes bares lanchonetes hoteis etc':
    'Comercialização em Canais Convencionais (Mercados, Hotéis)',

  // Comercialização eletrônica
  'programa projeto acao voltada a comercializacao eletronica dos produtos da agricultura familiar aplicativos sites midias sociais etc':
    'Comercialização Eletrônica (Sites, Apps)',

  // Declaração de produção (API usa texto longo com "beneficiário da reforma agrária")
  'emissao de declaracao de producao e localizacao de estabelecimento agropecuario para beneficiario da reforma agraria com vistas a obtencao de inscricao estadual':
    'Declaração de Produção para Inscrição Estadual',

  // Ações específicas (API usa "Programa/projeto/ação específica para ...")
  'programa projeto acao especifica para a juventude rural':
    'Ações para Juventude Rural',
  'programa projeto acao especifica para as mulheres rurais':
    'Ações para Mulheres Rurais',
  'programa projeto acao especifica para os indigenas':
    'Ações para Povos Indígenas',
  'programa projeto acao especifica para os quilombolas':
    'Ações para Quilombolas',
  'programa projeto acao especifica para outros povos e comunidades tradicionais':
    'Ações para Outros Povos e Comunidades',

  // Artesanato (API usa "Programa/projeto/ação voltada ao artesanato")
  'programa projeto acao voltada ao artesanato':
    'Artesanato Rural',

  // Extrativismo (API usa "Programa/projeto/ação voltada ao extrativismo...")
  'programa projeto acao voltada ao extrativismo de produtos florestais nao madeireiros sociobiodiversidade':
    'Extrativismo e Sociobiodiversidade',

  // Recuperação ambiental (API usa "Recuperação/recomposição ambiental")
  'recuperacao recomposicao ambiental de areas degradadas alteradas':
    'Recuperação de Áreas Degradadas',
};

// Adiciona entradas automáticas a partir de ORDEM_SERVICOS_PRESTADOS
// (para nomes da API que já coincidem com os rótulos canônicos)
ORDEM_SERVICOS_PRESTADOS.forEach((rotulo) => {
  const chave = normalizar(rotulo);
  if (!MAPEAMENTO_API[chave]) {
    MAPEAMENTO_API[chave] = rotulo;
  }
});
// Entradas especiais
MAPEAMENTO_API['nenhum servico e prestado'] = 'Nenhum serviço é prestado';
MAPEAMENTO_API['outros'] = 'Outros';

/**
 * Retorna o rótulo canônico para um nome_servico vindo da API.
 * 1. Tenta correspondência exata normalizada
 * 2. Tenta substring (chave de mapeamento contida no nome da API)
 * 3. Tenta substring inversa (nome da API contido na chave de mapeamento)
 * 4. Fallback: retorna o original
 */
const getRotuloServico = (nomeServico: string): string => {
  const norm = normalizar(nomeServico);

  // 1. Correspondência exata
  if (MAPEAMENTO_API[norm]) return MAPEAMENTO_API[norm];

  // 2. Chave contida no nome da API (API name é mais longo)
  for (const [chave, rotulo] of Object.entries(MAPEAMENTO_API)) {
    if (norm.includes(chave) && chave.length > 5) return rotulo;
  }

  // 3. Nome da API contido na chave (API name é mais curto)
  for (const [chave, rotulo] of Object.entries(MAPEAMENTO_API)) {
    if (chave.includes(norm) && norm.length > 5) return rotulo;
  }

  return nomeServico;
};

const sortServicosPrestados = (
  lista: { uuid: string; nome_servico: string }[],
): { uuid: string; nome_servico: string }[] => {
  return [...lista].sort((a, b) => {
    const nomeA = a.nome_servico ?? '';
    const nomeB = b.nome_servico ?? '';

    // "Nenhum" sempre primeiro, "Outros" sempre último
    const isNenhumA = nomeA.toLowerCase().includes('nenhum');
    const isNenhumB = nomeB.toLowerCase().includes('nenhum');
    const isOutrosA = nomeA.toLowerCase() === 'outros';
    const isOutrosB = nomeB.toLowerCase() === 'outros';

    if (isNenhumA) return -1;
    if (isNenhumB) return 1;
    if (isOutrosA) return 1;
    if (isOutrosB) return -1;

    const normA = normalizar(nomeA);
    const normB = normalizar(nomeB);

    const idxA = ORDEM_NORMALIZADA.findIndex((o) => normA.includes(o));
    const idxB = ORDEM_NORMALIZADA.findIndex((o) => normB.includes(o));

    const posA = idxA === -1 ? ORDEM_SERVICOS_PRESTADOS.length : idxA;
    const posB = idxB === -1 ? ORDEM_SERVICOS_PRESTADOS.length : idxB;

    return posA - posB;
  });
};

export const useStep4 = () => {
  const [uuidOutros, setUuidOutros] = useState<string | null>(null);
  const [uuidNenhum, setUuidNenhum] = useState<string | null>(null);
  const {
    formulario,
    listaServicosPrestados,
    setListaServicosPrestados,
    setPartialForm,
    salvarRascunho,
  } = useFormularioSecretariaMunicipalAgriculturaStore();

  const stepAtual = formulario?.step_atual ?? 1;
  const totalSteps = 8;

  const handleGetListaServicosPrestados = async () => {
    const data = await getListaServicosPrestados();
    setListaServicosPrestados(data.dados || []);
  };

  useEffect(() => {
    const outrosServico = listaServicosPrestados.find(
      (item) => normalizar(item.nome_servico).includes('outros') && !normalizar(item.nome_servico).includes('povos'),
    );
    if (outrosServico) {
      setUuidOutros(outrosServico.uuid);
    }

    const nenhum = listaServicosPrestados.find(
      (item) => normalizar(item.nome_servico).includes('nenhum'),
    );
    if (nenhum) {
      setUuidNenhum(nenhum.uuid);
    }
  }, [listaServicosPrestados]);

  useEffect(() => {
    handleGetListaServicosPrestados();
  }, []);

  const handleChangeServicoPrestado = (uuid: string) => {
    const servicosSelecionados = [...(formulario?.servicos_prestados || [])];

    if (servicosSelecionados.includes(uuid)) {
      const novosServicosPrestados = servicosSelecionados.filter(
        (item) => item !== uuid,
      );
      setPartialForm('servicos_prestados', novosServicosPrestados);

      if (uuidOutros !== null && uuid === uuidOutros) {
        setPartialForm('possui_outro_servico_prestado', false);
        setPartialForm('outro_servico_prestado', '');
      }
    } else {
      // If clicking on "Nenhum serviço é prestado", clear all others
      if (uuid === uuidNenhum) {
        setPartialForm('servicos_prestados', []);
      } else {
        // If clicking on any other item, remove "Nenhum" if it was selected
        const novosServicosPrestados = servicosSelecionados.filter(
          (item) => item !== uuidNenhum,
        );
        novosServicosPrestados.push(uuid);
        setPartialForm('servicos_prestados', novosServicosPrestados);

        if (uuidOutros !== null && uuid === uuidOutros) {
          setPartialForm('possui_outro_servico_prestado', true);
        }
      }
    }
  };

  const handleOtherServiceInputChange = (value: string) => {
    setPartialForm('outro_servico_prestado', value);
  };

  const handleClearServicosPrestados = () => {
    setPartialForm('servicos_prestados', []);
  };

  const isValid =
    formulario?.fundo_municipal_agricultura_familiar === false ||
    (
      formulario?.tipo_publicacao !== null
    );

  const handleAvancar = async () => {
    if (!isValid) return;
    setPartialForm('step_atual', stepAtual + 1);
    await salvarRascunho();
  };

  const handleVoltar = async () => {
    setPartialForm('step_atual', Math.max(1, stepAtual - 1));
    await salvarRascunho();
  };

  const listaServicosPrestadosOrdenada = useMemo(
    () =>
      sortServicosPrestados(listaServicosPrestados).map((item) => ({
        ...item,
        nome_servico: getRotuloServico(item.nome_servico),
      })),
    [listaServicosPrestados],
  );

  return {
    formulario,
    listaServicosPrestados: listaServicosPrestadosOrdenada,
    uuidNenhum,
    handleChangeServicoPrestado,
    handleOtherServiceInputChange,
    handleClearServicosPrestados,
    isValid,
    handleAvancar,
    handleVoltar,
    stepAtual,
    totalSteps,
  };
};
